import React, { Component } from 'react';
import PropTypes from 'prop-types';

import Text from 'components/Text';

import './input.styles.scss';

/** Styled input with WCAG enabled */
export default class FormInput extends Component {
    constructor(props) {
        super(props);
        this.state = {
            validate: props.validate,
            validation: {valid: true, error: ''},
            value: props.value || '',
        };
    }

    // We need to re-validate the field that has dependencies on other fields
    // Before re-validating, we need to check if the validation rules are the same as before
    // A dependency can change based on the field value that it depends on
    // e.g: company number is required only if VAT field is empty
    componentDidUpdate(prevProps, prevState) {
        const { value, validator, name } = this.props;

        // Log validation error to analytics
        if (prevState.validation.valid && !this.state.validation.valid) {
            const errorKey = this.state.validation.error;
            window.Checkout.analytics.logInputValidationError(name, errorKey, this.state.value);
        }


        if (!this.state.validate) {
            return;
        }

        // We don't have to validate if the rules didn't change
        if (Object.keys(prevProps.validator).length === Object.keys(validator).length) {
            // Even if the validator length is the same as before, the values of the dependencies might change
            const diff = Object.keys(prevProps.validator).filter(rule => prevProps.validator[rule] !== validator[rule]);
            if (!diff.length) {
                // there are no differences between the old and new rules.
                // in this case, we don't have to trigger the validation
                return;
            }
        }

        // Only trigger validation if we have an actual value
        if (value) {
            // Trigger validation of the field
            this.validateField(value);
        }
    }

    onChange = (event) => {
        const { onChange, name } = this.props;

        const value = event.target.value;
        this.setState({
            value: value,
        });

        // Trigger validation and save result in valid state
        if (this.state.validate) {
            this.validateField(value);
        }

        // Trigger on change event property
        if (typeof onChange === 'function') {
            onChange(name, value);
        }
    }

    onBlur = (event) => {
        // Trigger validation and save result in valid state
        if (this.state.validate) {
            this.validateField(event.target.value);
        }
    }

    static getDerivedStateFromProps(props) {
        return props;
    }

    renderError() {
        const { displayError } = this.props;
        if (!this.state.validation.valid || displayError) {
            const options = this.state.validation.textParams || null;
            return (
                <p className="error-helper" id={this.props.id + '_error'}>
                    <Text textKey={displayError ? displayError : this.state.validation.error} options={options} />
                </p>
            );
        }

        return null;
    }

    render() {
        const { displayError, type, placeholder, name, id, ariaLabel, ariaRequired, onChange, validate, validator, forwardRef, ...other } = this.props; // eslint-disable-line no-unused-vars
        let errorClass = '';

        if (!this.state.validation.valid || displayError) {
            errorClass = 'error-input';
        }

        let element;
        const commonAttributes = {
            className: errorClass,
            id: id,
            name: name,
            placeholder: placeholder,
            'aria-label': ariaLabel,
            'aria-required': ariaRequired,
            'aria-describedby': !this.state.validation.valid ? this.props.id + '_error' : '',
            'aria-invalid': !this.state.validation.valid,
            onChange: this.onChange.bind(this),
            onBlur: this.onBlur.bind(this),
            value: this.state.value,
            ref: forwardRef,
            ...other,
        };

        if (type === 'textarea') {
            element = React.createElement(type, commonAttributes);
        } else {
            commonAttributes.type = type;
            element = React.createElement('input', commonAttributes);
        }

        return (
            <div>
                {element}
                {this.renderError()}
            </div>
        );
    }

    validateField(value) {
        const { validator } = this.props;
        this.setState({ 'validation': this.state.validate(value, validator)});
    }
}

FormInput.propTypes = {
    /** Input type */
    type: PropTypes.string.isRequired,
    /** Placeholder */
    placeholder: PropTypes.string,
    /** Input name */
    name: PropTypes.string.isRequired,
    /** Aria label that describes the input to screen readers. */
    ariaLabel: PropTypes.string.isRequired,
    /** If field is required, this should be set to true */
    ariaRequired: PropTypes.bool,
    /** Input ID */
    id: PropTypes.string.isRequired,
    value: PropTypes.any,
    /** Callback function to be called when input text is changed */
    onChange: PropTypes.func,
    /** Callback called to validate input. Return {valid:false, error: 'Error message'} if error, false if it's valid */
    validate: PropTypes.func,
    validator: PropTypes.object,
    forwardRef: PropTypes.object,
    displayError: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.bool,
    ]),
};
